<script setup>
import { ref, computed, watch, onUnmounted, nextTick } from 'vue'
import { useGuide } from './useGuide'
import { getConnectionPoints, generateArrowPath } from './arrows'

const { _rawState: state, next, back, cancel, complete } = useGuide()

const targetRect = ref(null)
const labelRef = ref(null)
const arrow = ref(null)
const requirementMet = ref(false)

const PADDING = 8
const LABEL_OFFSET = 80

// Seeded PRNG for deterministic label jitter per step
function mulberry32(seed) {
  return function () {
    seed |= 0
    seed = (seed + 0x6d2b79f5) | 0
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function getLabelJitter(stepIndex) {
  const rng = mulberry32(stepIndex * 777 + 13)
  return {
    along: (rng() - 0.5) * 60,   // ±30px along the placement axis
    offset: (rng() - 0.5) * 40,  // ±20px perpendicular to placement
    dist: (rng() - 0.5) * 30,    // ±15px distance variation
  }
}

let scrollListener = null
let animFrameId = null
let requirementPollInterval = null

const currentStep = computed(() => {
  if (!state.active || !state.steps.length) return null
  return state.steps[state.currentIndex]
})

const showOverlay = computed(() => state.active)

// Overlay opacity: hidden during fade-out and clean phases
const overlayOpacity = computed(() => {
  const phase = state.transitionPhase
  if (phase === 'fade-out' || phase === 'clean') return 0
  return 1
})

// Label visible only when not transitioning, or in 'entering' phase
const labelVisible = computed(() => {
  const phase = state.transitionPhase
  if (!phase) return true
  if (phase === 'entering') return true
  return false
})

// Four-box overlay rects: top, right, bottom, left around the cutout
const overlayBoxes = computed(() => {
  const w = window.innerWidth
  const h = window.innerHeight
  if (!targetRect.value) {
    // No target — single full-screen overlay
    return [{ top: 0, left: 0, width: w, height: h }]
  }
  const c = {
    x: targetRect.value.x - PADDING,
    y: targetRect.value.y - PADDING,
    width: targetRect.value.width + PADDING * 2,
    height: targetRect.value.height + PADDING * 2,
  }
  return [
    // Top
    { top: 0, left: 0, width: w, height: Math.max(0, c.y) },
    // Bottom
    { top: c.y + c.height, left: 0, width: w, height: Math.max(0, h - c.y - c.height) },
    // Left
    { top: c.y, left: 0, width: Math.max(0, c.x), height: c.height },
    // Right
    { top: c.y, left: c.x + c.width, width: Math.max(0, w - c.x - c.width), height: c.height },
  ]
})

// Whether the target area should be interactive
const targetInteractive = computed(() => {
  const step = currentStep.value
  if (!step) return false
  return !!(step.canClickTarget || step.watchInput)
})

// Whether this step has a requirement that must be met before Next
const stepHasRequirement = computed(() => {
  const step = currentStep.value
  if (!step) return false
  return !!(step.watchInput || step.advanceOn || step.requireRoute)
})

// Whether a button should be disabled (unless step has onNext to perform the action)
function isButtonDisabled(btn) {
  if (btn.action !== 'next' && btn.action !== 'complete') return false
  if (!stepHasRequirement.value) return false
  if (requirementMet.value) return false
  // If step has onNext, the button is always clickable (it performs the action)
  if (currentStep.value?.onNext) return false
  return true
}

// SVG viewBox for arrows only
const svgViewBox = computed(() => `0 0 ${window.innerWidth} ${window.innerHeight}`)

// Label position
const labelStyle = computed(() => {
  if (!currentStep.value) return {}
  if (!targetRect.value) {
    return {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
    }
  }

  const placement = currentStep.value.attachTo?.on || currentStep.value.placement || 'bottom'
  const t = targetRect.value
  const jitter = getLabelJitter(state.currentIndex)
  const offsetDist = LABEL_OFFSET + jitter.dist
  let top, left

  switch (placement) {
    case 'top':
      top = t.y - PADDING - offsetDist
      left = t.x + t.width / 2 + jitter.along
      return { position: 'fixed', top: `${top}px`, left: `${left}px`, transform: 'translate(-50%, -100%)' }
    case 'bottom':
      top = t.y + t.height + PADDING + offsetDist
      left = t.x + t.width / 2 + jitter.along
      return { position: 'fixed', top: `${top}px`, left: `${left}px`, transform: 'translate(-50%, 0)' }
    case 'left':
      top = t.y + t.height / 2 + jitter.along
      left = t.x - PADDING - offsetDist
      return { position: 'fixed', top: `${top}px`, left: `${left}px`, transform: 'translate(-100%, -50%)' }
    case 'right':
      top = t.y + t.height / 2 + jitter.along
      left = t.x + t.width + PADDING + offsetDist
      return { position: 'fixed', top: `${top}px`, left: `${left}px`, transform: 'translate(0, -50%)' }
    default:
      return { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }
  }
})

// Watch step changes
watch(
  () => state.active && state.currentIndex,
  () => {
    if (state.active) {
      nextTick(() => {
        updateTarget()
        setupRequirementCheck()
      })
    }
  }
)

watch(showOverlay, (active) => {
  if (active) {
    nextTick(() => {
      updateTarget()
      setupRequirementCheck()
      startTracking()
    })
  } else {
    stopTracking()
    clearRequirementCheck()
  }
})

function updateTarget() {
  arrow.value = null
  const step = currentStep.value
  if (!step?.attachTo?.element) {
    targetRect.value = null
    return
  }

  const el = document.querySelector(step.attachTo.element)
  if (!el) {
    targetRect.value = null
    return
  }

  const rect = el.getBoundingClientRect()
  targetRect.value = {
    x: rect.x,
    y: rect.y,
    width: rect.width,
    height: rect.height,
  }

  nextTick(() => computeArrow())
}

function computeArrow() {
  if (!labelRef.value || !targetRect.value) {
    arrow.value = null
    return
  }

  const labelRect = labelRef.value.getBoundingClientRect()
  const tRect = {
    x: targetRect.value.x - PADDING,
    y: targetRect.value.y - PADDING,
    width: targetRect.value.width + PADDING * 2,
    height: targetRect.value.height + PADDING * 2,
  }

  const { from, to } = getConnectionPoints(labelRect, tRect)
  arrow.value = generateArrowPath(from, to, state.currentIndex * 1000 + 42)
}

function startTracking() {
  let frameCount = 0
  const initialLoop = () => {
    updateTarget()
    frameCount++
    if (frameCount < 10) {
      animFrameId = requestAnimationFrame(initialLoop)
    }
  }
  animFrameId = requestAnimationFrame(initialLoop)

  scrollListener = () => updateTarget()
  window.addEventListener('scroll', scrollListener, true)
  window.addEventListener('resize', scrollListener)
}

function stopTracking() {
  if (animFrameId) {
    cancelAnimationFrame(animFrameId)
    animFrameId = null
  }
  if (scrollListener) {
    window.removeEventListener('scroll', scrollListener, true)
    window.removeEventListener('resize', scrollListener)
    scrollListener = null
  }
  clearRequirementCheck()
}

function setupRequirementCheck() {
  clearRequirementCheck()
  requirementMet.value = false

  const step = currentStep.value
  if (!step) return

  // No requirement — next is always enabled
  if (!step.watchInput && !step.advanceOn && !step.requireRoute) {
    requirementMet.value = true
    return
  }

  // Poll for requirement fulfillment
  requirementPollInterval = setInterval(() => {
    if (checkRequirement(step)) {
      requirementMet.value = true
      clearInterval(requirementPollInterval)
      requirementPollInterval = null

      // Auto-advance for interaction-driven steps, but not when user went back
      if ((step.requireRoute || step.advanceOn) && !state.wentBack) {
        next()
      }
    }
  }, 200)

  // Focus input if watchInput step
  if (step.watchInput) {
    setTimeout(() => {
      const el = document.querySelector(step.watchInput.selector)
      if (el) {
        el.focus()
        el.click()
      }
    }, 100)
  }
}

function checkRequirement(step) {
  // watchInput: check if input contains expected value
  if (step.watchInput) {
    const el = document.querySelector(step.watchInput.selector)
    if (!el) return false
    return el.value.includes(step.watchInput.value)
  }
  // advanceOn: check if requirement was met via the advanceOn listener (tracked in useGuide)
  if (step.advanceOn) {
    return state.requirementMet === true
  }
  // requireRoute: check if current URL matches
  if (step.requireRoute) {
    return window.location.pathname.startsWith(step.requireRoute)
  }
  return true
}

function clearRequirementCheck() {
  if (requirementPollInterval) {
    clearInterval(requirementPollInterval)
    requirementPollInterval = null
  }
}

function handleAction(action) {
  const step = currentStep.value

  if ((action === 'next' || action === 'complete') && stepHasRequirement.value && !requirementMet.value) {
    // Requirement not met — run onNext to perform the action, then wait
    if (step?.onNext) {
      step.onNext()
    }
    return
  }

  switch (action) {
    case 'next': next(); break
    case 'back': back(); break
    case 'cancel': cancel(); break
    case 'complete': complete(); break
  }
}

onUnmounted(() => {
  stopTracking()
})
</script>

<template>
  <Teleport to="body">
    <div v-if="showOverlay" class="guide-overlay">
      <!-- Four-box overlay: darkened areas around the target -->
      <div
        v-for="(box, i) in overlayBoxes"
        :key="i"
        class="guide-overlay-box"
        :style="{
          top: box.top + 'px',
          left: box.left + 'px',
          width: box.width + 'px',
          height: box.height + 'px',
          opacity: overlayOpacity,
        }"
      />

      <!-- SVG layer for arrows only (no pointer events) -->
      <svg v-if="arrow && labelVisible" class="guide-arrow-svg" :viewBox="svgViewBox" preserveAspectRatio="none">
        <path
          :d="arrow.path"
          fill="none"
          stroke="#a78bfa"
          stroke-width="2"
          stroke-linecap="butt"
          :stroke-dasharray="arrow.length"
          :stroke-dashoffset="arrow.length"
          class="guide-arrow-path"
        />
        <path
          :d="arrow.headPath"
          fill="none"
          stroke="#a78bfa"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="guide-arrow-head"
        />
      </svg>

      <!-- Block clicks in cutout for non-interactive steps -->
      <div
        v-if="targetRect && !targetInteractive"
        class="guide-cutout-block"
        :style="{
          top: (targetRect.y - PADDING) + 'px',
          left: (targetRect.x - PADDING) + 'px',
          width: (targetRect.width + PADDING * 2) + 'px',
          height: (targetRect.height + PADDING * 2) + 'px',
          opacity: overlayOpacity,
        }"
      />

      <!-- Text label -->
      <div
        v-if="currentStep"
        ref="labelRef"
        class="guide-label"
        :class="{ 'guide-label-hidden': !labelVisible }"
        :style="labelStyle"
      >
        <div class="guide-label-text" v-html="currentStep.text"></div>

        <!-- Buttons -->
        <div v-if="currentStep.buttons" class="guide-buttons">
          <button
            v-for="(btn, i) in currentStep.buttons"
            :key="i"
            class="guide-btn"
            :class="{ primary: btn.primary, disabled: isButtonDisabled(btn) }"
            :disabled="isButtonDisabled(btn)"
            @click="handleAction(btn.action)"
          >
            {{ btn.text }}
          </button>
        </div>
      </div>

      <!-- Persistent progress widget -->
      <div class="guide-widget">
        <div class="guide-widget-header">
          <span class="guide-widget-step">Step {{ state.currentIndex + 1 }} of {{ state.steps.length }}</span>
          <button class="guide-widget-close" @click="cancel()" title="Exit guide">&times;</button>
        </div>
        <div class="guide-widget-track">
          <div class="guide-widget-bar" :style="{ width: ((state.currentIndex + 1) / state.steps.length * 100) + '%' }"></div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.guide-overlay {
  position: fixed;
  inset: 0;
  z-index: 9998;
  pointer-events: none;
}

.guide-overlay-box {
  position: fixed;
  background: rgba(0, 0, 0, 0.65);
  pointer-events: auto;
  transition: opacity 300ms ease;
}

.guide-arrow-svg {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 9999;
  pointer-events: none;
}

.guide-cutout-block {
  position: fixed;
  z-index: 9999;
  pointer-events: auto;
}

.guide-label {
  z-index: 10001;
  max-width: 360px;
  pointer-events: auto;
  background: var(--surface-raised);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 1.25rem 1.5rem;
  box-shadow: var(--shadow-lg);
  transition: opacity 200ms ease;
  opacity: 1;
}

.guide-label-hidden {
  opacity: 0;
  pointer-events: none;
}

.guide-label-text {
  color: var(--text-primary);
  font-size: 0.9rem;
  line-height: 1.6;
  font-family: 'Inter', sans-serif;
}

.guide-label-text :deep(code) {
  background: var(--accent-soft-3);
  color: var(--accent-text);
  padding: 0.15rem 0.4rem;
  border-radius: var(--radius-xs);
  font-size: 0.8rem;
}

.guide-label-text :deep(strong) {
  color: var(--accent-text);
  font-weight: 600;
}

.guide-buttons {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
}

.guide-btn {
  font-family: 'Inter', sans-serif;
  font-size: 0.8rem;
  font-weight: 500;
  border-radius: var(--radius-md);
  padding: 0.5rem 1.25rem;
  cursor: pointer;
  transition: var(--transition);
  background: var(--surface-raised);
  color: var(--text-muted);
  border: 1px solid var(--border-strong);
}

.guide-btn:hover {
  background: var(--surface-raised);
  color: var(--text-primary);
}

.guide-btn.primary {
  background: var(--accent);
  color: var(--text-heading);
  border: 1px solid var(--accent);
}

.guide-btn.primary:hover:not(:disabled) {
  background: var(--accent);
  color: var(--text-heading);
}

.guide-btn.disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

/* Arrow draw-in animation */
.guide-arrow-path {
  animation: guide-draw-arrow 400ms ease forwards;
}

@keyframes guide-draw-arrow {
  to {
    stroke-dashoffset: 0;
  }
}

.guide-arrow-head {
  opacity: 0;
  animation: guide-show-head 100ms ease 350ms forwards;
}

@keyframes guide-show-head {
  to {
    opacity: 1;
  }
}

/* Persistent progress widget */
.guide-widget {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 10002;
  pointer-events: auto;
  background: var(--surface-raised);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  padding: 0.625rem 0.875rem;
  box-shadow: var(--shadow-card-hover);
  min-width: 140px;
}

.guide-widget-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.guide-widget-step {
  color: var(--text-muted);
  font-size: 0.75rem;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  white-space: nowrap;
}

.guide-widget-close {
  background: none;
  border: none;
  color: var(--text-faint);
  font-size: 1.125rem;
  line-height: 1;
  cursor: pointer;
  padding: 0 0.125rem;
  transition: color 0.2s;
}

.guide-widget-close:hover {
  color: var(--text-primary);
}

.guide-widget-track {
  margin-top: 0.375rem;
  height: 3px;
  background: var(--surface-overlay-3);
  border-radius: var(--radius-xs);
  overflow: hidden;
}

.guide-widget-bar {
  height: 100%;
  background: var(--accent-text);
  border-radius: var(--radius-xs);
  transition: width 400ms ease;
}
</style>
